import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    Divider,
    Alert,
    CircularProgress,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useCart } from '../../contexts/CartContext';
import { ReservationStatus, PaymentStatus, MongoReservation } from '../../types/index';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise, finalizeReservation } from '../../services/paymentService';
import StripeCheckoutForm from './StripeCheckoutForm';
import api from '../../services/api';

const Payment: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { items: cart, clearCart } = useCart();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [reservationToCreate, setReservationToCreate] = useState<Omit<MongoReservation, '_id'> | null>(null);

    const reservationData = location.state?.reservation;
    const multiSessions = location.state?.multiSessions;
    const isMultiSession = location.state?.isMultiSession || false;
    const totalAmount = useMemo(() => cart.reduce((sum, item) => sum + item.price, 0), [cart]);

    useEffect(() => {
        if (reservationData) {
            try {
                const customerName = `${reservationData.customer.firstName} ${reservationData.customer.lastName}`;

                if (!reservationData.timeSlot || !reservationData.timeSlot._id) {
                    throw new Error("L'ID du créneau horaire est manquant");
                }

                const newReservation: Omit<MongoReservation, '_id'> = {
                    product: reservationData.product._id,
                    date: reservationData.date,
                    timeSlot: {
                        _id: reservationData.timeSlot._id,
                        date: reservationData.timeSlot.date,
                        time: reservationData.timeSlot.time,
                        startTime: reservationData.timeSlot.startTime,
                        endTime: reservationData.timeSlot.endTime || reservationData.timeSlot.time,
                        capacity: reservationData.timeSlot.capacity,
                        isAvailable: reservationData.timeSlot.isAvailable
                    },
                    participants: reservationData.participants,
                    status: 'pending' as ReservationStatus,
                    customer: {
                        name: customerName,
                        firstName: reservationData.customer.firstName,
                        lastName: reservationData.customer.lastName,
                        email: reservationData.customer.email,
                        phone: reservationData.customer.phone,
                        level: reservationData.customer.level
                    },
                    payment: {
                        status: 'pending' as PaymentStatus,
                        amount: totalAmount,
                        method: 'card'
                    }
                };

                setReservationToCreate(newReservation);
            } catch (err) {
                setError("Erreur de traitement des données de réservation");
            }
        }
    }, [reservationData, cart, totalAmount]);

    const handlePaymentSuccess = async (paymentIntentId: string) => {
        try {
            setLoading(true);

            if (isMultiSession && multiSessions) {
                const reservationsToCreate = multiSessions.map((session: any) => {
                    const customerName = `${session.customer.firstName} ${session.customer.lastName}`;
                    return {
                        product: session.product._id,
                        date: session.date,
                        timeSlot: {
                            _id: session.timeSlot._id,
                            date: session.timeSlot.date,
                            time: session.timeSlot.time,
                            startTime: session.timeSlot.startTime,
                            endTime: session.timeSlot.endTime,
                            capacity: session.timeSlot.capacity,
                            isAvailable: session.timeSlot.isAvailable
                        },
                        participants: session.participants,
                        status: 'pending' as ReservationStatus,
                        customer: {
                            name: customerName,
                            firstName: session.customer.firstName,
                            lastName: session.customer.lastName,
                            email: session.customer.email,
                            phone: session.customer.phone,
                            level: session.customer.level
                        },
                        payment: {
                            status: 'pending' as PaymentStatus,
                            amount: totalAmount / multiSessions.length,
                            method: 'card'
                        }
                    };
                });

                const response = await api.post('/reservations/finalize-multi', {
                    reservations: reservationsToCreate,
                    paymentIntentId
                });

                clearCart();
                navigate('/payment/success', { 
                    state: { 
                        reservation: response.data.reservations[0],
                        multiReservations: response.data.reservations,
                        isMultiSession: true
                    } 
                });
            } else {
                if (!reservationToCreate) {
                    throw new Error('Données de réservation non disponibles');
                }

                if (!reservationToCreate.timeSlot || !reservationToCreate.timeSlot._id) {
                    throw new Error("ID du créneau horaire manquant");
                }

                const reservationWithPayment: Omit<MongoReservation, '_id'> = {
                    ...reservationToCreate,
                    status: 'confirmed' as ReservationStatus,
                    payment: {
                        ...reservationToCreate.payment,
                        status: 'paid' as PaymentStatus,
                        paymentIntentId: paymentIntentId
                    }
                };

                const savedReservation = await finalizeReservation(reservationWithPayment, paymentIntentId);

                clearCart();
                navigate('/payment/success', { state: { reservation: savedReservation } });
            }
        } catch (error) {
            setError(error instanceof Error ? error.message : 'Une erreur est survenue');
        } finally {
            setLoading(false);
        }
    };

    if (!reservationData && !reservationToCreate) {
        return (
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Alert severity="warning">
                        Aucune donnée de réservation disponible. Veuillez retourner à la page précédente.
                    </Alert>
                </Paper>
            </Container>
        );
    }

    if (cart.length === 0) {
        return (
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                <Paper elevation={3} sx={{ p: 4 }}>
                    <Alert severity="warning">
                        Votre panier est vide. Veuillez ajouter des articles avant de procéder au paiement.
                    </Alert>
                </Paper>
            </Container>
        );
    }

    if (loading) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Paiement
                </Typography>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={4}>
                    <Grid item xs={12} md={6}>
                        <Elements stripe={stripePromise}>
                            <StripeCheckoutForm
                                onPaymentSuccess={handlePaymentSuccess}
                                onPaymentError={(errorMessage) => setError(errorMessage)}
                                amount={totalAmount}
                                productId={cart.length > 0 ? cart[0].id : ''}
                                reservation={reservationToCreate}
                            />
                        </Elements>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Typography variant="h6" gutterBottom>
                            Récapitulatif de la commande
                        </Typography>
                        {cart.map((item, index) => (
                            <Box key={index} sx={{ mb: 2 }}>
                                <Typography variant="body1">
                                    {item.name} - {item.price}€
                                </Typography>
                            </Box>
                        ))}

                        {isMultiSession && multiSessions && (
                            <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
                                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                    Dates sélectionnées ({multiSessions.length} plongées)
                                </Typography>
                                <List dense>
                                    {multiSessions.map((session: any, index: number) => (
                                        <ListItem key={index} sx={{ py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <CalendarTodayIcon fontSize="small" color="primary" />
                                            </ListItemIcon>
                                            <ListItemText 
                                                primary={`${new Date(session.date).toLocaleDateString('fr-FR', { 
                                                    weekday: 'long', 
                                                    day: 'numeric', 
                                                    month: 'long' 
                                                })} à ${session.timeSlot.time}`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}

                        <Divider sx={{ my: 2 }} />
                        <Typography variant="h6">
                            Total: {totalAmount}€
                        </Typography>
                    </Grid>
                </Grid>
            </Paper>
        </Container>
    );
};

export default Payment;
